import React, { useState, useEffect } from "react"
import { Route, Redirect } from "react-router-dom"
import { connect } from "react-redux"
import { compose } from "redux"
import useReactRouter from "use-react-router"
import PropTypes from "prop-types"

import Menu from "./../components/menu/menu"
import { clearFlags } from "../redux/actions/journey-actions"
import routes from "./routes"
import { useEFSData } from "hooks/efs-input"
import { useEFSContinue } from "hooks/efs-continue"

const JourneyRouter = ({
  nextPageFlag,
  prevPageFlag,
  firstPageFlag,
  submitFlag,
  unsetPageFlags,
  jumpPageFlag,
  resetJourneyFlag
}) => {
  const efsdata = useEFSData()
  const [currentPage, setCurrentPage] = useState(0)
  const { EFSSubmit: submit } = useEFSContinue()
  const { history } = useReactRouter()

  const route = routes.find(route => {
    return (
      (!route.conditionFunc || route.conditionFunc(efsdata)) &&
      ((route.conditions && route.conditions.includes(efsdata.condition)) ||
        route.condition === efsdata.condition)
    )
  })

  const getPageName = pageNumber => {
    return `/${pageNumber}`
  }

  // Sort out the page movements based on redux store actions
  useEffect(() => {
    // Define page movement functions
    const gotoPageWithFunction = deltaFunc => {
      const nextPage = deltaFunc(currentPage)

      setCurrentPage(nextPage)

      // and then push to the history
      history.push(getPageName(nextPage))
    }

    const gotoNextPage = () => {
      gotoPageWithFunction(i => i + 1)
    }

    const jumpPage = () => {
      gotoPageWithFunction(i => i + 2)
    }

    const gotoPrevPage = () => {
      gotoPageWithFunction(i => i - 1)
    }

    const gotoFirstPage = () => {
      // TODO: Replace this whole function with one which goes to a tagged page
      // HACK: Change to go to plan select page
      gotoPageWithFunction(() => 2)
    }

    const restartJourney = () => {
      gotoPageWithFunction(() => 0)
    }

    // Check to see if a next page or prev page flag has been set
    if (nextPageFlag) {
      unsetPageFlags()
      gotoNextPage()
    } else if (resetJourneyFlag) {
      unsetPageFlags()
      restartJourney()
    } else if (prevPageFlag) {
      unsetPageFlags()
      gotoPrevPage()
    } else if (firstPageFlag) {
      unsetPageFlags()
      gotoFirstPage()
    } else if (submitFlag) {
      unsetPageFlags()
      submit()
    } else if (jumpPageFlag) {
      unsetPageFlags()
      jumpPage()
    }
  }, [
    currentPage,
    submit,
    history,
    nextPageFlag,
    prevPageFlag,
    firstPageFlag,
    submitFlag,
    jumpPageFlag,
    unsetPageFlags,
    resetJourneyFlag
  ])

  // Cancel out of the render if we are processing page movement flags
  if (nextPageFlag || prevPageFlag || firstPageFlag) {
    return null
  }

  // Go through the routes, adding a page number to each page,
  // dropping the pages without a menu item component
  // and then just grabbing the data we want for the menu
  const menuRouteData = route.pages
    .map((page, index) => ({ ...page, pageNumber: index }))
    .filter(
      pageData =>
        pageData.menuItemText ||
        typeof pageData.menuItemComponent !== "undefined" ||
        pageData.noMenu
    )
    .map(stepData => ({
      pageNumber: stepData.pageNumber, // the page number this step is about
      Component: stepData.menuItemComponent, // Use capitals since we want React and JSX to instantiate
      text: stepData.menuItemText, // Text to display if we haven't gone past this page yet
      noMenu: stepData.noMenu, // The menu itself will determine whether to hide or not -- feels wrong though
      showBasketButton: stepData.showBasketButton, // whether the basket should have a button to go to the next page
      showDevice: stepData.showDevice, // whether it should show the device under the menu or not
      appendStoreName: stepData.appendStoreName // Whether to append the store name to the step text
    }))

  return (
    <>
      <Menu steps={menuRouteData} currentPageNumber={currentPage} />
      <Route exact path="/" render={() => <Redirect to="/0" />} key="start" />
      {route.pages.map(({ pageComponent }, i) => (
        <Route
          path={getPageName(i)}
          key={getPageName(i)}
          component={pageComponent}
        />
      ))}
    </>
  )
}

JourneyRouter.propTypes = {
  nextPageFlag: PropTypes.bool.isRequired,
  prevPageFlag: PropTypes.bool.isRequired,
  firstPageFlag: PropTypes.bool.isRequired,
  submitFlag: PropTypes.bool.isRequired,
  jumpPageFlag: PropTypes.bool.isRequired,
  unsetPageFlags: PropTypes.func.isRequired,
  resetJourneyFlag: PropTypes.bool.isRequired
}

// Get the Redux journey flags

const mapStateToProps = state => ({
  nextPageFlag: state.journey.gotoNextPage,
  prevPageFlag: state.journey.gotoPrevPage,
  firstPageFlag: state.journey.gotoFirstPage,
  submitFlag: state.journey.submitResults,
  jumpPageFlag: state.journey.jumpPage,
  resetJourneyFlag: state.journey.resetJourney
})

const mapDispatchToProps = dispatch => ({
  unsetPageFlags: () => dispatch(clearFlags())
})

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  JourneyRouter
)
